import * as XLSX from 'xlsx';

const HEADER_HINTS = new Set([
  'görev',
  'gorev',
  'task',
  'tasks',
  'madde',
  'checklist',
  'açıklama',
  'aciklama',
  'item',
  'items',
  'başlık',
  'baslik',
  'name',
  'title',
  '#',
  'no',
  'sıra',
  'sira',
]);

const DAY_NAMES = new Set([
  'pazartesi',
  'salı',
  'sali',
  'çarşamba',
  'carsamba',
  'perşembe',
  'persembe',
  'cuma',
  'cumartesi',
  'pazar',
]);

const DEFAULT_SHEET_NAME = 'Genel';
const PRODUCT_COLS = [1, 4, 7];
const CATEGORY_COLS = [0, 3, 6];

function cellText(value) {
  if (value == null) return '';
  return String(value).trim();
}

function isHeaderRow(cells) {
  const normalized = cells.map((c) => cellText(c).toLowerCase());
  return normalized.some((c) => HEADER_HINTS.has(c));
}

function isDayName(text) {
  return DAY_NAMES.has(text.toLowerCase().trim());
}

function isSkipTask(text) {
  const t = text.trim();
  if (!t || t.length < 3) return true;
  const lower = t.toLowerCase();
  if (HEADER_HINTS.has(lower)) return true;
  if (isDayName(t)) return true;
  if (/^örnektir$/i.test(t)) return true;
  if (/^saat$/i.test(lower)) return true;
  if (/^\d{1,2}:\d{2}$/.test(t)) return true;
  if (/^\.\.\s*\/\s*\.\./.test(t)) return true;
  if (/^tarih:/i.test(t)) return true;
  if (/^make me joi/i.test(t)) return true;
  if (/^tamamlandı$/i.test(t)) return true;
  if (/^kontrol edilecekler$/i.test(t)) return true;
  if (/^ilgili ay ve hafta/i.test(t)) return true;
  return false;
}

function findGorevColumnIndex(rows) {
  for (let i = 0; i < Math.min(rows.length, 20); i += 1) {
    const cells = (rows[i] || []).map((c) => cellText(c).toLowerCase());
    const idx = cells.findIndex((c) => c === 'görev' || c === 'gorev');
    if (idx >= 0) return idx;
  }
  return pickTaskColumnIndex(rows);
}

function pickTaskColumnIndex(rows) {
  if (!rows.length) return 0;

  const header = rows[0].map((c) => cellText(c).toLowerCase());
  const görevIdx = header.findIndex((c) => ['görev', 'gorev', 'task', 'madde', 'checklist', 'açıklama', 'aciklama'].includes(c));
  if (görevIdx >= 0) return görevIdx;

  if (rows.length > 1 && rows[0].length >= 2) {
    const sample = rows.slice(isHeaderRow(rows[0]) ? 1 : 0, 8);
    const col0Numeric = sample.filter((row) => row[0] != null && row[0] !== '' && !Number.isNaN(Number(row[0]))).length;
    const col1Text = sample.filter((row) => cellText(row[1]).length > 0).length;
    if (col1Text >= Math.max(1, sample.length / 2) && col0Numeric >= col1Text / 2) {
      return 1;
    }
  }

  return 0;
}

function collectUniqueTasks(texts) {
  const tasks = [];
  const seen = new Set();
  texts.forEach((text) => {
    if (isSkipTask(text)) return;
    const key = text.toLowerCase();
    if (seen.has(key)) return;
    seen.add(key);
    tasks.push(text);
  });
  return tasks;
}

function parseGorevColumnTasks(rows) {
  const colIdx = findGorevColumnIndex(rows);
  const texts = rows.map((row) => cellText((row || [])[colIdx]));
  return collectUniqueTasks(texts);
}

function parseHaftalikTasks(rows) {
  const texts = rows.map((row) => cellText((row || [])[1]));
  return collectUniqueTasks(texts);
}

function parseBarDisiTasks(rows) {
  let colIdx = 4;
  for (let i = 0; i < Math.min(rows.length, 4); i += 1) {
    const row = rows[i] || [];
    const idx = row.findIndex((c) => cellText(c).toLowerCase().includes('kontrol edilecek'));
    if (idx >= 0) {
      colIdx = idx;
      break;
    }
  }

  const texts = [];
  rows.forEach((row) => {
    const text = cellText((row || [])[colIdx]);
    if (!text) return;
    if (!/^\d+\s*[-–.]/.test(text)) return;
    texts.push(text);
  });
  return collectUniqueTasks(texts);
}

function parseBarIciProductTasks(rows) {
  const texts = [];
  const seen = new Set();

  for (let i = 2; i < rows.length; i += 1) {
    const row = rows[i] || [];
    const categories = new Set(
      CATEGORY_COLS.map((c) => cellText(row[c]).toLowerCase()).filter(Boolean),
    );

    PRODUCT_COLS.forEach((col) => {
      const text = cellText(row[col]);
      if (!text || text.length < 3) return;
      if (categories.has(text.toLowerCase())) return;
      const key = text.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      texts.push(text);
    });
  }

  return texts;
}

function isFormOnlySheet(sheetName) {
  const name = sheetName.toLowerCase();
  return name.includes('kullanım') || name.includes('kullanim') || name.includes('zayi');
}

function parseSheetTasks(sheetName, rows) {
  const name = sheetName.toLowerCase();

  if (isFormOnlySheet(sheetName)) {
    return { tasks: [], skipped: true, reason: 'Form şablonu (görev listesi değil)' };
  }
  if (name.includes('bar içi') || name.includes('bar ici') || name.includes('ürün listesi') || name.includes('urun listesi')) {
    return { tasks: parseBarIciProductTasks(rows), skipped: false };
  }
  if (name.includes('bar dışı') || name.includes('bar disi')) {
    return { tasks: parseBarDisiTasks(rows), skipped: false };
  }
  if (name.includes('haftalık') || name.includes('haftalik')) {
    return { tasks: parseHaftalikTasks(rows), skipped: false };
  }
  return { tasks: parseGorevColumnTasks(rows), skipped: false };
}

/** @deprecated kept for simple single-column sheets */
export function parseChecklistExcelRows(rows) {
  return parseGorevColumnTasks(rows);
}

/** Parse all worksheets from an Excel workbook. */
export function parseChecklistWorkbook(arrayBuffer) {
  const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: false });
  if (!workbook.SheetNames?.length) {
    throw new Error('Excel dosyasında sayfa bulunamadı.');
  }

  const sheets = [];
  const skippedSheets = [];

  workbook.SheetNames.forEach((name, index) => {
    const sheet = workbook.Sheets[name];
    const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
    const { tasks, skipped, reason } = parseSheetTasks(name, rows);
    const sheetName = cellText(name) || `Sayfa ${index + 1}`;

    if (skipped) {
      skippedSheets.push({ name: sheetName, reason: reason || 'Atlandı' });
      return;
    }

    if (!tasks.length) {
      skippedSheets.push({ name: sheetName, reason: 'Görev bulunamadı' });
      return;
    }

    sheets.push({
      name: sheetName,
      order: index,
      tasks,
    });
  });

  if (!sheets.length) {
    throw new Error('Excel dosyasında içe aktarılabilir görev bulunamadı.');
  }

  return { sheets, skippedSheets, sheetNames: workbook.SheetNames };
}

export function buildChecklistInsertRows({
  sheets,
  shopId,
  userId,
  existingTasks = [],
  importMode = 'replace',
}) {
  const rowsToInsert = [];

  for (const sheet of sheets) {
    if (!sheet.tasks.length) continue;
    const existing = importMode === 'replace'
      ? []
      : existingTasks.filter((t) => normalizeSheetName(t.sheet_name) === sheet.name);
    let startOrder = existing.length
      ? Math.max(...existing.map((t) => t.sort_order)) + 1
      : 0;

    sheet.tasks.forEach((text, index) => {
      rowsToInsert.push({
        user_id: userId,
        shop_id: shopId,
        text,
        sheet_name: sheet.name,
        sheet_order: sheet.order,
        sort_order: startOrder + index,
      });
    });
  }

  return rowsToInsert;
}

/** @deprecated Use parseChecklistWorkbook — first sheet tasks only */
export function parseChecklistExcelFile(arrayBuffer) {
  const { sheets } = parseChecklistWorkbook(arrayBuffer);
  return sheets[0]?.tasks || [];
}

export function buildChecklistTemplateWorkbook() {
  const workbook = XLSX.utils.book_new();

  const opening = XLSX.utils.aoa_to_sheet([
    ['Görev'],
    ['Kasa açılış sayımı'],
    ['Kahve makinesi ısıtma ve test'],
    ['Vitrin düzeni kontrolü'],
  ]);
  opening['!cols'] = [{ wch: 42 }];
  XLSX.utils.book_append_sheet(workbook, opening, 'Açılış');

  const midday = XLSX.utils.aoa_to_sheet([
    ['Görev'],
    ['Tuvalet ve oturma alanı kontrolü'],
    ['Stok sayımı — süt ve kahve'],
    ['Personel mola planı'],
  ]);
  midday['!cols'] = [{ wch: 42 }];
  XLSX.utils.book_append_sheet(workbook, midday, 'Gün içi');

  const closing = XLSX.utils.aoa_to_sheet([
    ['Görev'],
    ['Gün sonu kasa kapanışı'],
    ['Ekipman temizliği'],
    ['Alarm ve kapı kontrolü'],
  ]);
  closing['!cols'] = [{ wch: 42 }];
  XLSX.utils.book_append_sheet(workbook, closing, 'Kapanış');

  return workbook;
}

export function downloadChecklistTemplate() {
  const workbook = buildChecklistTemplateWorkbook();
  XLSX.writeFile(workbook, 'gunluk-checklist-sablonu.xlsx');
}

export function normalizeSheetName(name) {
  const trimmed = cellText(name);
  return trimmed || DEFAULT_SHEET_NAME;
}

export { DEFAULT_SHEET_NAME };
